import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaSatelliteDish, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/auth";
import {
  cargarDatosAlertasRecurrencia,
  calcularAlertasRecurrencia,
  formatMontoCompacto,
  motivoAlerta,
} from "../../utils/alertasRecurrencia";
import { descartarAlerta } from "../../utils/alertasRecurrenciaDescarte";
import { useCurrency } from "../../utils/useCurrency";
import styles from "./stylesAlertasRecurrencia.module.scss";

// "Radar Comercial" del Home (/admin) — docs/modulos/alertas-recurrencia-comercial.md. Entra en la
// misma familia que Proyección Comercial y Comisión Comercial (Ignacio, 2026-09-25) — el nombre
// completo deja claro qué barre. El ámbar es deliberado: el rojo queda reservado para los
// contadores de deuda real (Facturación, Caja); un cliente para retomar es una oportunidad, no un
// error. Vive DENTRO del <Header> vía `centerContent`, AL MEDIO (Admin/index.js arma el contenedor
// que lo centra mientras <CumpleanosBanner /> se queda pegado a la derecha).
//
// EL PANEL SE RENDERIZA EN UN PORTAL A document.body (fix 2026-09-25, ver ficha "motivo real del
// bug de apilado"): `.headerCenterZoneCentro` (stylesAdmin.module.scss) tiene `transform:
// translateX(-50%)` para centrar el botón — y CUALQUIER `transform` crea un nuevo stacking context
// Y ADEMÁS convierte a ese elemento en el containing block de sus descendientes `position:fixed`
// (spec CSS, no un detalle de este componente). Por eso NI subir el z-index NI cambiar a
// `position:fixed` sin portal alcanzaba: el panel seguía atrapado dentro de esa caja transformada,
// compitiendo solo contra sus hermanos ahí adentro, nunca contra el resto de la página. El portal
// saca el nodo del DOM entero a un hijo directo de `<body>`, fuera del subárbol transformado — ahí
// `position:fixed` sí resuelve contra el viewport real. Coordenadas medidas con
// `getBoundingClientRect()` sobre el botón disparador, recalculadas en resize/scroll mientras está
// abierto (ver `actualizarCoords`/el efecto de listeners).
export const AlertasRecurrenciaBanner = () => {
  const { pais, id } = useAuthContext();
  const { formatAmount } = useCurrency();
  const navigate = useNavigate();
  const [alertas, setAlertas] = useState([]);
  const [descarteDisponible, setDescarteDisponible] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState(null); // {top, left} en coordenadas de viewport (fixed)
  const triggerRef = useRef(null); // el botón "Radar Comercial" — mide posición + outside-click
  const panelRef = useRef(null); // el panel portalado — outside-click

  useEffect(() => {
    if (!pais || pais === "Region") return undefined;
    let vivo = true;
    cargarDatosAlertasRecurrencia({ pais })
      .then(({ oportunidades, clientes, resumenActividad, descartes }) => {
        if (!vivo) return;
        setDescarteDisponible(descartes !== null); // null = tabla todavía no aplicada (10.137)
        setAlertas(calcularAlertasRecurrencia({ oportunidades, clientes, resumenActividad, descartes, pais }));
      })
      .catch(() => {
        // Sin alertas visibles ante error de carga — nunca rompe el Home (10.177).
        if (vivo) setAlertas([]);
      });
    return () => {
      vivo = false;
    };
  }, [pais]);

  const PANEL_WIDTH = 640;
  const MARGEN = 8;

  const actualizarCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const anchoDisponible = Math.min(PANEL_WIDTH, window.innerWidth - MARGEN * 2);
    let left = rect.left + rect.width / 2 - anchoDisponible / 2;
    left = Math.max(MARGEN, Math.min(left, window.innerWidth - anchoDisponible - MARGEN));
    setCoords({ top: rect.bottom + 6, left, width: anchoDisponible });
  }, []);

  // Mide ANTES del paint (useLayoutEffect) para que el panel no aparezca un frame en (0,0).
  useLayoutEffect(() => {
    if (abierto) actualizarCoords();
  }, [abierto, actualizarCoords]);

  // Reposiciona mientras está abierto: la ventana puede cambiar de tamaño o la página puede
  // scrollear (el botón se mueve en pantalla aunque su posición en el documento no cambie).
  // `scroll` con capture:true agarra el scroll de CUALQUIER contenedor anidado, no solo window.
  useEffect(() => {
    if (!abierto) return undefined;
    window.addEventListener("resize", actualizarCoords);
    window.addEventListener("scroll", actualizarCoords, true);
    return () => {
      window.removeEventListener("resize", actualizarCoords);
      window.removeEventListener("scroll", actualizarCoords, true);
    };
  }, [abierto, actualizarCoords]);

  // Cierre al click afuera: ahora hay que chequear DOS nodos (el botón y el panel portalado ya no
  // son el mismo subárbol del DOM desde que el panel vive en document.body).
  useEffect(() => {
    if (!abierto) return undefined;
    const handler = (e) => {
      const fueraDelBoton = triggerRef.current && !triggerRef.current.contains(e.target);
      const fueraDelPanel = panelRef.current && !panelRef.current.contains(e.target);
      if (fueraDelBoton && fueraDelPanel) setAbierto(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [abierto]);

  if (alertas.length === 0) return null;

  const irAEmpresa = (alerta) => {
    setAbierto(false);
    navigate("/admin/ventas/crm", { state: { filtroEmpresaAlerta: alerta.empresaDisplay } });
  };

  const descartar = async (e, alerta) => {
    e.stopPropagation();
    const clave = `${alerta.empresaNorm}|${alerta.mesObjetivo}|${alerta.anoObjetivo}`;
    setAlertas((prev) => prev.filter((a) => `${a.empresaNorm}|${a.mesObjetivo}|${a.anoObjetivo}` !== clave));
    try {
      await descartarAlerta({
        pais,
        empresaNorm: alerta.empresaNorm,
        mes: alerta.mesObjetivo,
        ano: alerta.anoObjetivo,
        usuarioId: id,
      });
    } catch {
      // El descarte no se pudo guardar — la alerta ya se sacó de la vista de esta sesión igual;
      // si recarga la página, vuelve a aparecer (10.177: no se finge un descarte que no se guardó).
    }
  };

  const montoTotal = alertas.reduce((suma, a) => suma + a.monto, 0);

  const panel = abierto && coords && (
    <div className={styles.panel} ref={panelRef} style={{ top: coords.top, left: coords.left, width: coords.width }}>
      <div className={styles.encabezado}>
        <div className={styles.encabezadoTitulo}>
          El Radar Comercial encontró {alertas.length} {alertas.length === 1 ? "cliente" : "clientes"}.{" "}
          {formatMontoCompacto(montoTotal)} entre todos.
        </div>
        <div className={styles.encabezadoCriterio}>
          El año pasado compraron o cotizaron en estos meses y este año todavía no.
        </div>
      </div>
      <div className={styles.lista}>
        {alertas.map((alerta) => {
          const clave = `${alerta.empresaNorm}-${alerta.mesObjetivo}-${alerta.anoObjetivo}`;
          return (
            <div key={clave} className={styles.fila} onClick={() => irAEmpresa(alerta)}>
              <span className={`${styles.punto} ${alerta.tipo === "compro" ? styles.puntoVerde : styles.puntoGris}`} />
              <span className={styles.colEmpresa} title={alerta.empresaDisplay}>
                {alerta.empresaDisplay}
              </span>
              <span className={styles.colMotivo} title={motivoAlerta(alerta)}>
                {motivoAlerta(alerta)}
              </span>
              <span className={styles.colMonto}>{formatAmount(alerta.monto)}</span>
              {descarteDisponible && (
                <button
                  type="button"
                  className={styles.descartar}
                  onClick={(e) => descartar(e, alerta)}
                  title="Descartar hasta el año que viene"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className={styles.pie}>
        <span className={styles.pieLlave}>
          <span className={`${styles.punto} ${styles.puntoVerde}`} /> Compró
        </span>
        <span className={styles.pieLlave}>
          <span className={`${styles.punto} ${styles.puntoGris}`} /> Se propuso
        </span>
        <span className={styles.pieDescarte}>La cruz lo descarta hasta el año que viene</span>
      </div>
    </div>
  );

  return (
    <div className={styles.contenedor} ref={triggerRef}>
      <button type="button" className={styles.boton} onClick={() => setAbierto((v) => !v)}>
        <FaSatelliteDish className={styles.icono} aria-label="radar" />
        <span className={styles.label}>Radar Comercial</span>
        <span className={styles.badge}>{alertas.length}</span>
      </button>
      {panel && createPortal(panel, document.body)}
    </div>
  );
};
